import { PublishCourseResultDTO } from "../../dto/PublishCourseResultDTO";

export type PublishFailureTone = "error" | "retryable";

/** Codes the API returns with a 409: the course is fine, its assets are not ready yet. */
export const RETRYABLE_PUBLISH_CODES = [
  "assets_not_found",
  "asset_variants_not_processed",
] as const;

/** Client-side codes, used when the response carries no error payload. */
export const PUBLISH_SERVER_ERROR_CODE = "server_error";
export const PUBLISH_NO_RESPONSE_CODE = "no_response";
export const PUBLISH_UNKNOWN_CODE = "unknown";
/**
 * Publish auto-translates any required language that is missing. When that
 * request fails we stop rather than publish the course with empty names and
 * descriptions, which is what happened while the translation service swallowed
 * its own errors and returned nothing.
 */
export const PUBLISH_TRANSLATION_FAILED_CODE = "translation_failed";

export interface PublishFailure {
  /** error.code from the API, or one of the client-side codes above. Rendered verbatim. */
  code: string;
  /** error.message from the API, shown verbatim. Empty when the server sent none. */
  message: string;
  /** HTTP status. 0 means there was no response at all. */
  status: number;
  /** Passthrough for the day the API adds it to the error envelope. */
  stage?: string;
}

/**
 * Approved English copy, keyed by code. Used as the i18n defaultValue so the
 * screen reads correctly before publish_page.errors.* exists in the translation
 * files; once those keys land, they take over and this stays as the fallback.
 */
export const PUBLISH_ERROR_HEADLINES: Record<string, string> = {
  training_not_found: "This training could not be found.",
  training_content_not_found: "The content of this training could not be loaded.",
  training_3d_no_environment: "This 3D training has no environment selected.",
  language_not_found: "One of the selected languages is not available.",
  training_no_languages_marked_for_publish:
    "No languages are marked for publishing.",
  training_localization_content_not_found:
    "The translations for this training could not be loaded.",
  training_compiling_failed: "This training could not be compiled.",
  assets_not_found: "Some assets in this course are not ready yet.",
  asset_variants_not_processed: "Some assets are still being processed.",
  asset_not_found: "An asset used in this course no longer exists.",
  asset_variant_not_found: "A version of one of your assets is missing.",
  asset_invalid: "One of your assets cannot be published.",
  course_not_created: "The course could not be created.",
  course_not_updated: "The course could not be updated.",
  asset_not_created: "The asset could not be created.",
  asset_not_updated: "The asset could not be updated.",
  asset_version_not_created:
    "A new version of one of your assets could not be created.",
  asset_version_not_updated: "An asset version could not be published.",
  asset_variant_not_created:
    "A variant of one of your assets could not be created.",
  course_assets_not_linked: "The assets could not be linked to the course.",
  [PUBLISH_SERVER_ERROR_CODE]: "Something went wrong on our side.",
  [PUBLISH_NO_RESPONSE_CODE]: "We could not reach the server.",
  [PUBLISH_UNKNOWN_CODE]: "Your course could not be published.",
  [PUBLISH_TRANSLATION_FAILED_CODE]:
    "The missing translations could not be generated.",
};

/** Second line, only for the codes where there is something useful to add. */
export const PUBLISH_ERROR_HINTS: Record<string, string> = {
  assets_not_found:
    "They are still being uploaded or processed. Wait a few minutes and publish again.",
  asset_variants_not_processed:
    "Processing usually takes a few minutes for video. Publish again once it is done.",
  [PUBLISH_SERVER_ERROR_CODE]: "Wait a few minutes and try again.",
  [PUBLISH_NO_RESPONSE_CODE]: "Check your connection and try again.",
  [PUBLISH_TRANSLATION_FAILED_CODE]:
    "Nothing was published, so the course still has its previous version. Try again, or fill the missing languages in yourself first.",
};

export const toneForCode = (code: string): PublishFailureTone =>
  (RETRYABLE_PUBLISH_CODES as readonly string[]).includes(code)
    ? "retryable"
    : "error";

/**
 * Turns a publish response into something the failure screen can render.
 * Classifies on the code first and only falls back to the HTTP status, so a
 * code the backend adds later still renders correctly without a Creator release.
 */
export const classifyPublishFailure = (
  result: PublishCourseResultDTO
): PublishFailure => {
  const message = result.message ?? "";

  // No response at all.
  if (result.status === 0) {
    return { code: PUBLISH_NO_RESPONSE_CODE, message, status: 0 };
  }

  if (result.code) {
    return {
      code: result.code,
      message,
      status: result.status,
      stage: result.stage,
    };
  }

  // No error payload: fall back on the status alone.
  return {
    code:
      result.status >= 500 ? PUBLISH_SERVER_ERROR_CODE : PUBLISH_UNKNOWN_CODE,
    message,
    status: result.status,
    stage: result.stage,
  };
};

export interface PublishSupportContext {
  organizationName?: string;
  organizationId?: string;
  trainingName?: string;
}

/**
 * Plain-text report for the Copy details button. Labels stay English on purpose:
 * this is a support artifact and uniform tickets beat a localized clipboard.
 */
export const buildSupportText = (
  failure: PublishFailure,
  context: PublishSupportContext = {}
): string => {
  const { organizationName, organizationId, trainingName } = context;

  const organization = [organizationName, organizationId && `(${organizationId})`]
    .filter(Boolean)
    .join(" ");

  const fields: [string, string | undefined][] = [
    ["Organization", organization || undefined],
    ["Training", trainingName],
    ["Status", failure.status === 0 ? "no response" : `HTTP ${failure.status}`],
    ["Code", failure.code],
    ["Stage", failure.stage],
    ["Message", failure.message || undefined],
  ];

  const present = fields.filter(([, value]) => !!value);
  const width = Math.max(...present.map(([label]) => label.length)) + 1;

  return [
    "PlayIt Creator - publish failure",
    ...present.map(([label, value]) => `${`${label}:`.padEnd(width + 1)}${value}`),
  ].join("\n");
};
